import React, { useState, useEffect, useRef } from 'react'
import outlookIcon from '../../media/outlook.svg'
import { useDarkMode } from '../../context/DarkModeContext'

const SMTP_CONFIGS = {
	custom: {
		label: 'Custom SMTP',
		host: '',
		port: '',
		secure: true,
	},
	yahoo: {
		label: 'Yahoo Mail',
		host: 'smtp.mail.yahoo.com',
		port: '465',
		secure: true,
	},
	zoho: {
		label: 'Zoho Mail',
		host: 'smtp.zoho.com',
		port: '465',
		secure: true,
	},
	godaddy: {
		label: 'GoDaddy',
		host: 'smtpout.secureserver.net',
		port: '465',
		secure: true,
	},
}

const ConnectEmailAccountModal = ({ isOpen, onClose, onAuthorize, allowOutlook = false, allowReadAccess = false, error = null, submitting = false }) => {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode
	const [showSmtpForm, setShowSmtpForm] = useState(false)
	const [showGmailScopes, setShowGmailScopes] = useState(false)
	const [showOutlookScopes, setShowOutlookScopes] = useState(false)
	const [selectedProvider, setSelectedProvider] = useState('custom')
	const [readAccess, setReadAccess] = useState(true)
	const modalRef = useRef(null)
	// The outside-click listener always calls the latest close handler.
	const handleCloseRef = useRef(null)
	const [smtpConfig, setSmtpConfig] = useState({
		email: '',
		host: '',
		port: '',
		username: '',
		password: '',
		secure: true,
		provider: 'custom',
	})

	useEffect(() => {
		if (selectedProvider) {
			const config = SMTP_CONFIGS[selectedProvider]
			setSmtpConfig(prev => ({
				...prev,
				host: config.host,
				port: config.port,
				secure: config.secure,
				provider: selectedProvider,
			}))
		}
	}, [selectedProvider])

	useEffect(() => {
		const handleClickOutside = event => {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				handleCloseRef.current()
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleClose = () => {
		setShowSmtpForm(false)
		setShowGmailScopes(false)
		setShowOutlookScopes(false)
		setSelectedProvider('custom')
		setSmtpConfig({
			email: '',
			host: '',
			port: '',
			username: '',
			password: '',
			secure: true,
			provider: 'custom',
		})
		onClose()
	}

	handleCloseRef.current = handleClose

	const handleGmailAuthorize = () => {
		onAuthorize('gmail', { readAccess: allowReadAccess && readAccess })
	}

	const handleOutlookAuthorize = () => {
		onAuthorize('outlook')
	}

	if (!isOpen) return null

	const handleSmtpSubmit = async e => {
		e.preventDefault()
		const connected = await onAuthorize('smtp', smtpConfig)
		if (!connected) return
		setSmtpConfig({
			email: '',
			host: '',
			port: '',
			username: '',
			password: '',
			secure: true,
			provider: 'custom',
		})
		setSelectedProvider('custom')
		setShowSmtpForm(false)
	}

	return (
		<div className="relative z-[100]">
			<div className="fixed inset-0 z-[90] bg-black transition-opacity duration-300 opacity-50" />

			<div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
				<div className="flex min-h-full items-start justify-center pt-32 sm:pt-[5.5rem] px-4">
					<div
						ref={modalRef}
						className={`relative transform overflow-hidden rounded-lg text-left transition-all w-full max-w-2xl ${
							isLight
								? 'bg-white shadow-lg ring-1 ring-gray-200'
								: 'bg-[#15202b] shadow-[0_0_15px_rgba(59,130,246,0.1)] ring-1 ring-white/10'
						}`}
					>
						<button
							onClick={handleClose}
							className={`absolute right-4 top-4 sm:right-6 sm:top-6 ${isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}`}
						>
							<svg
								className="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>

						<div className="px-5 pt-8 pb-6 sm:px-8 sm:pt-8 sm:pb-6">
							<div className="w-full">
								{error && (
									<div role="alert" className={`mb-4 px-4 py-3 rounded-lg text-sm ${isLight ? 'bg-red-500/10 text-red-600' : 'bg-red-500/10 text-red-400'}`}>
										{error}
									</div>
								)}
								{!showSmtpForm ? (
									<>
										{!showGmailScopes && !showOutlookScopes ? (
											<>
												<h3 className={`text-xl sm:text-2xl font-bold mb-6 ${isLight ? 'text-gray-900' : 'text-white'}`}>
													Connect an email account
												</h3>
												<div className="flex flex-col space-y-3">
													<button
														onClick={() => setShowGmailScopes(true)}
														className={`group flex w-full items-center gap-4 rounded-lg px-4 py-4 text-left transition-colors duration-150 ease-in-out ${
															isLight
																? 'bg-gray-50 hover:bg-gray-100 ring-1 ring-gray-200 hover:ring-gray-300'
																: 'bg-[#1a2634]/60 hover:bg-[#1a2634] ring-1 ring-white/10 hover:ring-white/20'
														}`}
													>
														<div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
															isLight
																? 'bg-white ring-1 ring-gray-200'
																: 'bg-[#263340] ring-1 ring-white/5'
														}`}>
															<svg className="w-5 h-5" viewBox="0 0 24 24">
																<path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
																<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
																<path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
																<path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
															</svg>
														</div>
														<div className="flex-1 min-w-0">
															<div className={`font-semibold text-base ${isLight ? 'text-gray-900' : 'text-white'}`}>Gmail</div>
															<div className={`text-sm truncate ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Google Workspace or personal Gmail</div>
														</div>
														<div className={`flex-shrink-0 flex items-center gap-2 text-xs font-mono transition-colors ${
															isLight
																? 'text-gray-500 group-hover:text-gray-700'
																: 'text-gray-500 group-hover:text-gray-300'
														}`}>
															<span className="hidden sm:inline">OAuth 2.0</span>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
															</svg>
														</div>
													</button>
													{allowOutlook && (
														<button
															onClick={() => setShowOutlookScopes(true)}
															className={`group flex w-full items-center gap-4 rounded-lg px-4 py-4 text-left transition-colors duration-150 ease-in-out ${
																isLight
																	? 'bg-gray-50 hover:bg-gray-100 ring-1 ring-gray-200 hover:ring-gray-300'
																	: 'bg-[#1a2634]/60 hover:bg-[#1a2634] ring-1 ring-white/10 hover:ring-white/20'
															}`}
														>
															<div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
																isLight
																	? 'bg-white ring-1 ring-gray-200'
																	: 'bg-[#263340] ring-1 ring-white/5'
															}`}>
																<img src={outlookIcon} alt="Outlook" className="w-6 h-6" />
															</div>
															<div className="flex-1 min-w-0">
																<div className={`font-semibold text-base ${isLight ? 'text-gray-900' : 'text-white'}`}>Outlook</div>
																<div className={`text-sm truncate ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Microsoft 365 or Outlook.com</div>
															</div>
															<div className={`flex-shrink-0 flex items-center gap-2 text-xs font-mono transition-colors ${
																isLight
																	? 'text-gray-500 group-hover:text-gray-700'
																	: 'text-gray-500 group-hover:text-gray-300'
															}`}>
																<span className="hidden sm:inline">OAuth 2.0</span>
																<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
																</svg>
															</div>
														</button>
													)}
													<button
														onClick={() => setShowSmtpForm(true)}
														className={`group flex w-full items-center gap-4 rounded-lg px-4 py-4 text-left transition-colors duration-150 ease-in-out ${
															isLight
																? 'bg-gray-50 hover:bg-gray-100 ring-1 ring-gray-200 hover:ring-gray-300'
																: 'bg-[#1a2634]/60 hover:bg-[#1a2634] ring-1 ring-white/10 hover:ring-white/20'
														}`}
													>
														<div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
															isLight
																? 'bg-white ring-1 ring-gray-200'
																: 'bg-[#263340] ring-1 ring-white/5'
														}`}>
															<svg className={`w-5 h-5 ${isLight ? 'text-gray-700' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 12h14M5 12a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 01-2 2M5 12a2 2 0 00-2 2v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
															</svg>
														</div>
														<div className="flex-1 min-w-0">
															<div className={`font-semibold text-base ${isLight ? 'text-gray-900' : 'text-white'}`}>Custom SMTP / IMAP</div>
															<div className={`text-sm truncate ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Any provider via standard mail protocols</div>
														</div>
														<div className={`flex-shrink-0 flex items-center gap-2 text-xs font-mono transition-colors ${
															isLight
																? 'text-gray-500 group-hover:text-gray-700'
																: 'text-gray-500 group-hover:text-gray-300'
														}`}>
															<span className="hidden sm:inline">Manual setup</span>
															<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
															</svg>
														</div>
													</button>
												</div>
											</>
										) : showGmailScopes || showOutlookScopes ? (
											<>
												<h3 className={`text-xl sm:text-2xl font-bold mb-6 ${isLight ? 'text-gray-900' : 'text-white'}`}>
													{showGmailScopes ? 'Gmail permissions' : 'Outlook permissions'}
												</h3>

												<div className={`text-[11px] font-mono uppercase tracking-[0.2em] mb-3 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
													Required access
												</div>

												<div className="space-y-3">
													<div className={`flex items-start gap-4 rounded-lg px-4 py-4 ${
														isLight
															? 'bg-gray-50 ring-1 ring-gray-200'
															: 'bg-[#1a2634]/60 ring-1 ring-white/10'
													}`}>
														<div className={`flex-shrink-0 mt-0.5 w-7 h-7 rounded-md flex items-center justify-center ${
															isLight
																? 'bg-white ring-1 ring-gray-200'
																: 'bg-[#263340] ring-1 ring-white/10'
														}`}>
															<svg className={`w-4 h-4 ${isLight ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
															</svg>
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 flex-wrap">
																<span className={`font-semibold text-base ${isLight ? 'text-gray-900' : 'text-white'}`}>Send emails on your behalf</span>
																<span className={`text-[10px] font-mono uppercase tracking-wider rounded px-2 py-0.5 ${
																	isLight
																		? 'text-gray-700 bg-white ring-1 ring-gray-200'
																		: 'text-gray-300 bg-[#263340] ring-1 ring-white/10'
																}`}>
																	Required
																</span>
															</div>
															<div className={`text-sm mt-1 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																Required to send messages from this mailbox.
															</div>
														</div>
													</div>

												</div>

												{showGmailScopes && allowReadAccess && (
													<>
														<div className={`text-[11px] font-mono uppercase tracking-[0.2em] mt-6 mb-3 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
															Optional access
														</div>
														<label className={`flex items-start gap-4 rounded-lg px-4 py-4 cursor-pointer ${
															isLight
																? 'bg-gray-50 ring-1 ring-gray-200'
																: 'bg-[#1a2634]/60 ring-1 ring-white/10'
														}`}>
															<input
																type="checkbox"
																checked={readAccess}
																onChange={e => setReadAccess(e.target.checked)}
																className="mt-1 h-4 w-4 flex-shrink-0 rounded accent-[#1d9bf0]"
															/>
															<div className="flex-1 min-w-0">
																<span className={`font-semibold text-base ${isLight ? 'text-gray-900' : 'text-white'}`}>Read and organize emails</span>
																<div className={`text-sm mt-1 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																	See broker replies in LoadFetcher and label the emails you send from it. You can connect without this.
																</div>
															</div>
														</label>
													</>
												)}
											</>
										) : null}
									</>
								) : (
									<>
										<h3 className={`text-xl sm:text-2xl font-bold mb-6 ${isLight ? 'text-gray-900' : 'text-white'}`}>
											Custom SMTP
										</h3>

										<div className={`flex border-b mb-6 ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
											{Object.entries(SMTP_CONFIGS).map(([key, config]) => {
												const active = selectedProvider === key
												return (
													<button
														key={key}
														type="button"
														role="tab"
														aria-selected={active}
														onClick={() => setSelectedProvider(key)}
														className={`relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors -mb-px border-b-2 ${
															active
																? (isLight ? 'text-gray-900 border-[#1d9bf0]' : 'text-white border-[#1d9bf0]')
																: (isLight ? 'text-gray-500 hover:text-gray-700 border-transparent' : 'text-gray-400 hover:text-gray-200 border-transparent')
														}`}
													>
														{config.label}
													</button>
												)
											})}
										</div>

										<form id="smtp-form" onSubmit={handleSmtpSubmit} className="space-y-4" autoComplete="off">
											<div>
												<label className={`block text-sm font-medium mb-1.5 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
													Email address
												</label>
												<input
													type="email"
													value={smtpConfig.email}
													onChange={e =>
														setSmtpConfig({
															...smtpConfig,
															email: e.target.value,
														})
													}
													className={`w-full px-3 py-2.5 rounded-lg focus:outline-none focus:ring-[#1d9bf0] ${
														isLight
															? 'bg-white ring-1 ring-gray-300 text-gray-900 placeholder-gray-400'
															: 'bg-[#1a2634]/60 ring-1 ring-white/10 text-white placeholder-gray-500'
													}`}
													required
												/>
											</div>
											<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
												<div className="sm:col-span-2">
													<label className={`block text-sm font-medium mb-1.5 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
														SMTP host
													</label>
													<input
														type="text"
														value={smtpConfig.host}
														onChange={e =>
															setSmtpConfig({
																...smtpConfig,
																host: e.target.value,
															})
														}
														className={`w-full px-3 py-2.5 rounded-lg focus:outline-none focus:ring-[#1d9bf0] read-only:opacity-70 ${
															isLight
																? 'bg-white ring-1 ring-gray-300 text-gray-900 placeholder-gray-400'
																: 'bg-[#1a2634]/60 ring-1 ring-white/10 text-white placeholder-gray-500'
														}`}
														required
														readOnly={selectedProvider !== 'custom'}
													/>
												</div>
												<div>
													<label className={`block text-sm font-medium mb-1.5 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
														Port
													</label>
													<input
														type="text"
														value={smtpConfig.port}
														onChange={e =>
															setSmtpConfig({
																...smtpConfig,
																port: e.target.value,
															})
														}
														className={`w-full px-3 py-2.5 rounded-lg focus:outline-none focus:ring-[#1d9bf0] read-only:opacity-70 ${
															isLight
																? 'bg-white ring-1 ring-gray-300 text-gray-900 placeholder-gray-400'
																: 'bg-[#1a2634]/60 ring-1 ring-white/10 text-white placeholder-gray-500'
														}`}
														required
														readOnly={selectedProvider !== 'custom'}
													/>
												</div>
											</div>
											<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
												<div>
													<label className={`block text-sm font-medium mb-1.5 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
														Username
													</label>
													<input
														type="text"
														value={smtpConfig.username}
														onChange={e =>
															setSmtpConfig({
																...smtpConfig,
																username: e.target.value,
															})
														}
														className={`w-full px-3 py-2.5 rounded-lg focus:outline-none focus:ring-[#1d9bf0] ${
															isLight
																? 'bg-white ring-1 ring-gray-300 text-gray-900 placeholder-gray-400'
																: 'bg-[#1a2634]/60 ring-1 ring-white/10 text-white placeholder-gray-500'
														}`}
														required
													/>
												</div>
												<div>
													<label className={`block text-sm font-medium mb-1.5 ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
														Password
													</label>
													<input
														type="password"
														autoComplete="new-password"
														value={smtpConfig.password}
														onChange={e =>
															setSmtpConfig({
																...smtpConfig,
																password: e.target.value,
															})
														}
														className={`w-full px-3 py-2.5 rounded-lg focus:outline-none focus:ring-[#1d9bf0] ${
															isLight
																? 'bg-white ring-1 ring-gray-300 text-gray-900 placeholder-gray-400'
																: 'bg-[#1a2634]/60 ring-1 ring-white/10 text-white placeholder-gray-500'
														}`}
														required
													/>
												</div>
											</div>

											<div className="flex items-center justify-between pt-1">
												<label htmlFor="smtpSecure" className={`text-sm font-medium cursor-pointer ${isLight ? 'text-gray-700' : 'text-gray-300'}`}>
													Use SSL/TLS
												</label>
												<button
													id="smtpSecure"
													type="button"
													role="switch"
													aria-checked={smtpConfig.secure}
													onClick={() =>
														setSmtpConfig({
															...smtpConfig,
															secure: !smtpConfig.secure,
														})
													}
													disabled={selectedProvider !== 'custom'}
													className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#1d9bf0]/40 ${
														smtpConfig.secure
															? 'bg-[#1d9bf0]'
															: (isLight ? 'bg-gray-200' : 'bg-[#263340]')
													}`}
												>
													<span
														className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-150 ${
															smtpConfig.secure ? 'translate-x-[1.125rem]' : 'translate-x-[0.1875rem]'
														}`}
													/>
												</button>
											</div>
										</form>
									</>
								)}
							</div>
						</div>

						<div className="px-8 py-4 flex items-center justify-between gap-3">
							{(showGmailScopes || showOutlookScopes || showSmtpForm) ? (
								<button
									type="button"
									onClick={() => {
										setShowGmailScopes(false)
										setShowOutlookScopes(false)
										setShowSmtpForm(false)
									}}
									className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${
										isLight
											? 'text-gray-500 hover:text-gray-700'
											: 'text-gray-400 hover:text-gray-200'
									}`}
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
									</svg>
									Back to providers
								</button>
							) : <div />}

							<div className="flex flex-row-reverse gap-3">
							{showSmtpForm ? (
								<>
									<button
										type="submit"
										form="smtp-form"
										disabled={submitting}
										className="disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2 justify-center rounded-lg bg-[#1d9bf0] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1a8cd8] focus:bg-[#1a8cd8] transition-colors duration-150 ease-in-out"
									>
										Connect
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
										</svg>
									</button>
									<button
										type="button"
										onClick={handleClose}
										className={`inline-flex justify-center rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors duration-150 ease-in-out ${
											isLight
												? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
												: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
										}`}
									>
										Close
									</button>
								</>
							) : showGmailScopes ? (
								<>
									<button
										type="button"
										onClick={handleGmailAuthorize}
										disabled={submitting}
										className="inline-flex items-center gap-2 justify-center rounded-lg bg-[#1d9bf0] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1a8cd8] transition-colors duration-150 ease-in-out"
									>
										Continue
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
										</svg>
									</button>
									<button
										type="button"
										onClick={handleClose}
										className={`inline-flex justify-center rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors duration-150 ease-in-out ${
											isLight
												? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
												: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
										}`}
									>
										Close
									</button>
								</>
							) : showOutlookScopes ? (
								<>
									<button
										type="button"
										onClick={handleOutlookAuthorize}
										disabled={submitting}
										className="inline-flex items-center gap-2 justify-center rounded-lg bg-[#1d9bf0] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1a8cd8] transition-colors duration-150 ease-in-out"
									>
										Continue
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
										</svg>
									</button>
									<button
										type="button"
										onClick={handleClose}
										className={`inline-flex justify-center rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors duration-150 ease-in-out ${
											isLight
												? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
												: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
										}`}
									>
										Close
									</button>
								</>
							) : (
								<button
									type="button"
									onClick={handleClose}
									className={`inline-flex justify-center rounded-lg px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors duration-150 ease-in-out ${
										isLight
											? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
											: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
									}`}
								>
									Close
								</button>
							)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ConnectEmailAccountModal
